import { useEffect, useMemo, useState } from 'react';
import {
  actionStatusLabel,
  actionTransitionLabel,
  canRecordBuilderResponse,
  canRecordFollowUp,
  getAllowedActionTransitions,
  isActionOverdue,
} from '../lib/actions';
import {
  loadActionForIssue,
  loadActionTimeline,
  recordBuilderResponse,
  recordFollowUp,
  saveAction,
  transitionAction,
} from '../services/actions';
import type {
  AccountableParty,
  ActionDraft,
  ActionEventRecord,
  ActionRecord,
  ActionStatus,
} from '../types/actions';
import type { IssuePriority, IssueRecord } from '../types/issues';

interface ActionWorkspaceProps {
  ownerId: string;
  issue: IssueRecord;
  onBack: () => void;
}

type AsyncStatus = 'loading' | 'ready' | 'saving' | 'error';

const parties: Array<{ value: AccountableParty; label: string }> = [
  { value: 'builder', label: 'Builder / developer' },
  { value: 'seller', label: 'Seller' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'society', label: 'Society / association' },
  { value: 'expert', label: 'Expert / contractor' },
  { value: 'other', label: 'Other' },
];

const priorities: IssuePriority[] = ['immediate', 'urgent', 'important', 'monitor', 'minor'];

function emptyDraft(issue: IssueRecord): ActionDraft {
  return {
    requestedAction: `Please inspect and rectify the observed issue: ${issue.title}.`,
    accountableParty: 'builder',
    accountablePartyName: '',
    priority: issue.priority,
    dueDate: '',
    followUpDate: '',
  };
}

function draftFromAction(action: ActionRecord): ActionDraft {
  return {
    requestedAction: action.requested_action,
    accountableParty: action.accountable_party,
    accountablePartyName: action.accountable_party_name ?? '',
    priority: action.priority,
    dueDate: action.due_date,
    followUpDate: action.follow_up_date ?? '',
  };
}

function formatDate(value: string | null): string {
  if (!value) return 'Not set';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(
    new Date(`${value}T00:00:00`),
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function eventLabel(event: ActionEventRecord): string {
  switch (event.event_type) {
    case 'created':
      return 'Action created';
    case 'submitted':
      return 'Marked submitted';
    case 'acknowledged':
      return 'Acknowledged';
    case 'builder_response':
      return 'Builder response recorded';
    case 'follow_up':
      return 'Follow-up recorded';
    case 'closed':
      return 'Verified closed';
    case 'waived':
      return 'Waived';
    default:
      return event.to_status
        ? `Status: ${actionStatusLabel(event.to_status)}`
        : event.event_type.replaceAll('_', ' ');
  }
}

function buildActionBrief(issue: IssueRecord, action: ActionRecord): string {
  return [
    'NivasaSaathi builder action',
    '',
    `Finding: ${issue.title}`,
    `Location: ${issue.location}`,
    `Priority: ${action.priority}`,
    `Requested correction: ${action.requested_action}`,
    `Accountable party: ${action.accountable_party_name || action.accountable_party}`,
    `Due date: ${formatDate(action.due_date)}`,
    `Follow-up date: ${formatDate(action.follow_up_date)}`,
    `Status: ${actionStatusLabel(action.status)}`,
    '',
    'This is a buyer-recorded action request, not a certification of defect or rectification.',
  ].join('\n');
}

export function ActionWorkspace({ ownerId, issue, onBack }: ActionWorkspaceProps) {
  const [action, setAction] = useState<ActionRecord | null>(null);
  const [timeline, setTimeline] = useState<ActionEventRecord[]>([]);
  const [draft, setDraft] = useState<ActionDraft>(() => emptyDraft(issue));
  const [status, setStatus] = useState<AsyncStatus>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [builderResponse, setBuilderResponse] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus('loading');
      setMessage(null);

      try {
        const existing = await loadActionForIssue(ownerId, issue.id);
        if (cancelled) return;

        setAction(existing);
        if (existing) {
          setDraft(draftFromAction(existing));
          const events = await loadActionTimeline(ownerId, existing.id);
          if (!cancelled) setTimeline(events);
        }
        setStatus('ready');
      } catch (caught) {
        if (!cancelled) {
          setStatus('error');
          setMessage(caught instanceof Error ? caught.message : 'Could not load this action.');
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [issue.id, ownerId]);

  const canSave =
    draft.requestedAction.trim().length >= 10 &&
    Boolean(draft.dueDate) &&
    status !== 'saving';

  const allowedTransitions = useMemo(
    () => (action ? getAllowedActionTransitions(action.status) : []),
    [action],
  );

  const builderResponseEnabled = action ? canRecordBuilderResponse(action.status) : false;
  const followUpEnabled = action ? canRecordFollowUp(action.status) : false;

  async function refreshTimeline(actionId: string) {
    const events = await loadActionTimeline(ownerId, actionId);
    setTimeline(events);
  }

  async function handleSave() {
    if (!canSave) {
      setMessage('Add a clear requested correction and due date before saving.');
      return;
    }

    setStatus('saving');
    setMessage(null);
    try {
      const saved = await saveAction(ownerId, issue, draft, action?.id);
      setAction(saved);
      await refreshTimeline(saved.id);
      setStatus('ready');
      setMessage(
        action
          ? 'Action updated.'
          : 'Action created. Review it before marking ready to submit.',
      );
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not save the action.');
    }
  }

  async function handleTransition(nextStatus: ActionStatus) {
    if (!action) return;
    setStatus('saving');
    setMessage(null);

    try {
      const updated = await transitionAction(ownerId, action, nextStatus);
      setAction(updated);
      setDraft(draftFromAction(updated));
      await refreshTimeline(updated.id);
      setStatus('ready');
      setMessage(
        nextStatus === 'submitted'
          ? 'Recorded as submitted. NivasaSaathi did not send anything automatically.'
          : `Action moved to ${actionStatusLabel(nextStatus)}.`,
      );
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not update action status.');
    }
  }

  async function handleBuilderResponse() {
    if (!action || !builderResponseEnabled) return;
    setStatus('saving');
    setMessage(null);

    try {
      const updated = await recordBuilderResponse(ownerId, action, builderResponse);
      setAction(updated);
      setBuilderResponse('');
      await refreshTimeline(updated.id);
      setStatus('ready');
      setMessage('Builder response saved to the permanent action timeline.');
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not save the builder response.');
    }
  }

  async function handleFollowUp() {
    if (!action || !followUpEnabled) return;
    setStatus('saving');
    setMessage(null);

    try {
      await recordFollowUp(ownerId, action, followUpNote);
      setFollowUpNote('');
      await refreshTimeline(action.id);
      setStatus('ready');
      setMessage('Follow-up note saved.');
    } catch (caught) {
      setStatus('error');
      setMessage(caught instanceof Error ? caught.message : 'Could not save the follow-up note.');
    }
  }

  async function copyBrief() {
    if (!action) return;
    try {
      await navigator.clipboard.writeText(buildActionBrief(issue, action));
      setMessage(
        'Builder action copied. Send it through your chosen channel, then mark submitted only after sending.',
      );
    } catch {
      setMessage('Copy was blocked by the browser. Select the action details manually instead.');
    }
  }

  return (
    <main className="page act-page">
      <button className="text-button" type="button" onClick={onBack}>
        ← Back to findings
      </button>

      <section className="act-hero">
        <div>
          <span className="eyebrow">Act</span>
          <h1>Turn this finding into an accountable correction request</h1>
          <p className="lead">
            Create one clear action, assign responsibility, set a due date and keep the follow-up history together.
          </p>
        </div>
        {action ? (
          <div className="act-status-stack">
            <span className={`priority-chip priority-${action.priority}`}>{action.priority}</span>
            <span className="act-status-chip">{actionStatusLabel(action.status)}</span>
            {isActionOverdue(action.due_date, action.status) ? (
              <span className="overdue-chip">Overdue</span>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="act-finding-context">
        <span className="eyebrow">Source finding</span>
        <h2>{issue.title}</h2>
        <p>{issue.description || 'No additional note recorded.'}</p>
        <div className="issue-meta">
          <span>{issue.location}</span>
          <span>{issue.priority}</span>
        </div>
      </section>

      {message ? <div className="status-banner" role="status">{message}</div> : null}
      {status === 'loading' ? <div className="status-banner">Loading action…</div> : null}

      <section className="act-form-card">
        <div>
          <span className="eyebrow">Correction request</span>
          <h2>{action ? 'Edit action details' : 'Create action'}</h2>
        </div>

        <label className="field">
          <span>Requested correction *</span>
          <textarea
            rows={4}
            value={draft.requestedAction}
            onChange={(event) =>
              setDraft((current) => ({ ...current, requestedAction: event.target.value }))
            }
            placeholder="Describe the correction you want the accountable party to inspect and address."
          />
        </label>

        <div className="field-grid">
          <label className="field">
            <span>Accountable party *</span>
            <select
              value={draft.accountableParty}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  accountableParty: event.target.value as AccountableParty,
                }))
              }
            >
              {parties.map((party) => (
                <option key={party.value} value={party.value}>{party.label}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Party / contact name</span>
            <input
              value={draft.accountablePartyName}
              onChange={(event) =>
                setDraft((current) => ({ ...current, accountablePartyName: event.target.value }))
              }
              placeholder="Example: Site CRM team"
            />
          </label>

          <label className="field">
            <span>Priority *</span>
            <select
              value={draft.priority}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  priority: event.target.value as IssuePriority,
                }))
              }
            >
              {priorities.map((priority) => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Due date *</span>
            <input
              type="date"
              value={draft.dueDate}
              onChange={(event) =>
                setDraft((current) => ({ ...current, dueDate: event.target.value }))
              }
            />
          </label>

          <label className="field">
            <span>Follow-up date</span>
            <input
              type="date"
              value={draft.followUpDate}
              onChange={(event) =>
                setDraft((current) => ({ ...current, followUpDate: event.target.value }))
              }
            />
          </label>
        </div>

        <button
          className="button button-primary"
          type="button"
          disabled={!canSave}
          onClick={() => void handleSave()}
        >
          {status === 'saving' ? 'Saving…' : action ? 'Save action changes' : 'Create action'}
        </button>
      </section>

      {action ? (
        <>
          <section className="act-control-card">
            <div>
              <span className="eyebrow">Action control</span>
              <h2>Move the action only when the real-world step has happened</h2>
              <p>
                NivasaSaathi records the workflow. It does not claim to send email or WhatsApp messages on your behalf.
              </p>
            </div>

            <div className="act-summary-grid">
              <div><span>Due</span><strong>{formatDate(action.due_date)}</strong></div>
              <div><span>Follow-up</span><strong>{formatDate(action.follow_up_date)}</strong></div>
              <div><span>Owner</span><strong>{action.accountable_party_name || action.accountable_party}</strong></div>
            </div>

            <div className="act-button-row">
              <button
                className="button button-secondary ink-button"
                type="button"
                onClick={() => void copyBrief()}
              >
                Copy builder action
              </button>
              {allowedTransitions.map((nextStatus) => (
                <button
                  key={nextStatus}
                  className={
                    nextStatus === 'submitted'
                      ? 'button button-primary'
                      : 'button button-secondary ink-button'
                  }
                  type="button"
                  disabled={status === 'saving'}
                  onClick={() => void handleTransition(nextStatus)}
                >
                  {actionTransitionLabel(nextStatus)}
                </button>
              ))}
            </div>

            {action.status === 'ready_to_submit' ? (
              <p className="inline-note">
                Copy or share the action outside NivasaSaathi first. Use “Mark as submitted” only after you actually send it.
              </p>
            ) : null}
            {action.status === 'rectification_claimed' ? (
              <p className="inline-note">
                A rectification claim is not closure. Recheck and closure evidence are handled in the separate Close workflow.
              </p>
            ) : null}
          </section>

          <section className="act-response-card">
            <span className="eyebrow">Builder response</span>
            <h2>Record what the accountable party said</h2>
            {!builderResponseEnabled ? (
              <p className="inline-note">
                Mark this action as submitted before recording a builder response.
              </p>
            ) : null}
            <textarea
              rows={3}
              value={builderResponse}
              disabled={!builderResponseEnabled || status === 'saving'}
              onChange={(event) => setBuilderResponse(event.target.value)}
              placeholder="Example: Site team acknowledged the seepage mark and committed to inspect on 18 Aug."
            />
            <button
              className="button button-secondary ink-button"
              type="button"
              disabled={
                !builderResponseEnabled ||
                status === 'saving' ||
                builderResponse.trim().length < 3
              }
              onClick={() => void handleBuilderResponse()}
            >
              Record builder response
            </button>
          </section>

          <section className="act-response-card">
            <span className="eyebrow">Follow-up</span>
            <h2>Add a follow-up note</h2>
            {!followUpEnabled ? (
              <p className="inline-note">
                Mark this action as submitted before recording follow-up activity.
              </p>
            ) : null}
            <textarea
              rows={3}
              value={followUpNote}
              disabled={!followUpEnabled || status === 'saving'}
              onChange={(event) => setFollowUpNote(event.target.value)}
              placeholder="Example: Followed up with CRM team, inspection visit still pending."
            />
            <button
              className="button button-secondary ink-button"
              type="button"
              disabled={
                !followUpEnabled || status === 'saving' || followUpNote.trim().length < 3
              }
              onClick={() => void handleFollowUp()}
            >
              Save follow-up
            </button>
          </section>

          <section className="act-timeline-card">
            <span className="eyebrow">Permanent timeline</span>
            <h2>Action history</h2>
            {timeline.length === 0 ? (
              <p>No action events recorded yet.</p>
            ) : (
              <ol className="act-timeline">
                {timeline.map((event) => (
                  <li key={event.id}>
                    <div>
                      <strong>{eventLabel(event)}</strong>
                      <span>{formatDateTime(event.created_at)}</span>
                    </div>
                    {event.note ? <p>{event.note}</p> : null}
                  </li>
                ))}
              </ol>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}
